<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="otherAsset.id" class="text-h5">Update other asset</h2>
              <h2 v-else class="text-h5">Add other asset</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="description"
                v-slot="{ errors }"
                name="description"
                rules="required"
              >
                <TextField
                  v-model.trim="otherAsset.description"
                  :label="'Description*'"
                  :errors="errors"
                  :name="'description'"
                  :type="'textarea'"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="otherAsset.id" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save()"
                />
                <GradientButton
                  :title="'Remove Other Asset'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>
            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save()"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        @delete="deleteOtherAsset"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_OTHER_ASSET_MUTATION,
    UPDATE_OTHER_ASSET_MUTATION,
    DELETE_OTHER_ASSET_MUTATION,
  } from '../../constants/graphql/other-assets';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'OtherAssetDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      otherAssetToUpdate: Object,
    },
    data() {
      return {
        otherAsset: {
          description: null,
        },
        deleteCheck: false,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        const text =
          'Are you sure you want to remove this asset?<br><b>' +
          this.otherAsset.displayName +
          '</b><br><br>' +
          this.deleteDonationWarning;
        return {
          title: 'Remove other asset',
          text: text,
          buttonText: 'Yes, Remove Asset',
        };
      },
      deleteDonationWarning() {
        if (this.otherAsset.specificDonation) {
          return '<b>Warning:</b> Donation of this asset to your desired beneficiaries will be deleted';
        } else {
          return '';
        }
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.otherAsset = JSON.parse(JSON.stringify(this.otherAssetToUpdate));
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.otherAsset.id) {
              this.updateOtherAsset();
            } else {
              this.createOtherAsset();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createOtherAsset() {
        const otherAssetInputDetails = {
          testatorId: this.testatorId,
          description: this.otherAsset.description,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_OTHER_ASSET_MUTATION,
            variables: {
              otherAssetInputDetails,
            },
            update: (cache, { data: { createOtherAsset } }) => {
              this.updateCacheAfterCreateOtherAsset(cache, createOtherAsset);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterCreateOtherAsset(cache, createOtherAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.otherAssets.push(createOtherAsset);

        cache.writeQuery({ ...query, data: data });
      },
      updateOtherAsset() {
        const otherAssetInputDetails = {
          description: this.otherAsset.description,
        };
        const id = this.otherAsset.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_OTHER_ASSET_MUTATION,
            variables: {
              id,
              otherAssetInputDetails,
            },
            update: (cache, { data: { updateOtherAsset } }) => {
              this.updateCacheAfterUpdateOtherAsset(cache, updateOtherAsset);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterUpdateOtherAsset(cache, updateOtherAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var otherAsset = data.testator.otherAssets.find(
          otherAsset => otherAsset.id == updateOtherAsset.id
        );
        otherAsset = updateOtherAsset;

        cache.writeQuery({ ...query, data: data });
      },
      deleteOtherAsset() {
        const id = this.otherAsset.id;

        this.$apollo
          .mutate({
            mutation: DELETE_OTHER_ASSET_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteOtherAsset } }) => {
              this.updateCacheAfterDeleteOtherAsset(cache, deleteOtherAsset);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteOtherAsset(cache, deleteOtherAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var otherAssets = data.testator.otherAssets;
        var index = otherAssets.findIndex(
          otherAsset => otherAsset.id == deleteOtherAsset.id
        );
        otherAssets.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.otherAsset = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
